import React, { useEffect, useMemo, useState } from "react";
import { api, inr, formatApiError, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import FileAttachment from "@/components/FileAttachment";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import PageExportBar from "@/components/PageExportBar";
import SearchableCustomerSelect from "@/components/SearchableCustomerSelect";
import {
  Note, Plus, Trash, PencilSimple, MagnifyingGlass, Broadcast, CheckCircle, Warning
} from "@phosphor-icons/react";

const today = () => new Date().toISOString().slice(0, 10);
const STATUSES = ["pending", "deposited", "cleared", "bounced", "cancelled"];
const STATUS_COLOR = {
  pending: "hsl(var(--warning))", deposited: "hsl(var(--info, 210 90% 55%))",
  cleared: "hsl(var(--success))", bounced: "hsl(var(--destructive))",
  cancelled: "hsl(var(--muted-foreground))",
};

const emptyForm = {
  direction: "received", number: "", drawn_on_bank: "",
  party_id: "", party_name: "", cheque_date: today(),
  deposit_date: "", clearance_date: "", amount: 0,
  status: "pending", voucher_id: "", note: "",
};

function ChequeForm({ open, setOpen, editing, onSaved }) {
  const { dateFloor } = useAuth();
  const [form, setForm] = useState(editing || emptyForm);
  const [parties, setParties] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(editing || emptyForm); }, [editing, open]);

  useEffect(() => {
    if (!open) return;
    api.get(form.direction === "received" ? "/customers" : "/suppliers")
      .then((r) => setParties(r.data)).catch(() => setParties([]));
  }, [form.direction, open]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, amount: Number(form.amount) || 0 };
      if (editing) await api.patch(`/cheques/${editing.id}`, payload);
      else await api.post("/cheques", payload);
      toast.success(editing ? "Cheque updated" : "Cheque added");
      setOpen(false);
      onSaved && onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">
            {editing ? "Edit Cheque" : "Add Cheque"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="eyebrow">Direction *</Label>
              <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v, party_id: "", party_name: "" })}>
                <SelectTrigger data-testid="cheque-direction"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="received">Received (from Customer)</SelectItem>
                  <SelectItem value="issued">Issued (to Supplier)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="eyebrow">Cheque #*</Label>
              <Input value={form.number} required
                onChange={(e) => setForm({ ...form, number: e.target.value })}
                className="font-mono" data-testid="cheque-number" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="eyebrow">Drawn on Bank</Label>
              <Input value={form.drawn_on_bank}
                onChange={(e) => setForm({ ...form, drawn_on_bank: e.target.value })}
                placeholder="ICICI / HDFC / SBI…" data-testid="cheque-bank" />
            </div>
            <div className="space-y-1.5">
              <Label className="eyebrow">{form.direction === "received" ? "Customer" : "Supplier"} *</Label>
              <SearchableCustomerSelect
                value={form.party_id}
                onChange={(v) => {
                  const p = parties.find((x) => x.id === v);
                  setForm({ ...form, party_id: v, party_name: p?.name || form.party_name });
                }}
                customers={parties}
                placeholder={`Search & select ${form.direction === "received" ? "customer" : "supplier"}`}
                testid="cheque-party"
                getSubtitle={(p) => p.gstin || p.phone}
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="eyebrow">Cheque Date *</Label>
              <Input type="date" value={form.cheque_date} min={dateFloor} required
                onChange={(e) => setForm({ ...form, cheque_date: e.target.value })}
                className="font-mono" data-testid="cheque-date" />
            </div>
            <div className="space-y-1.5">
              <Label className="eyebrow">Deposit Date</Label>
              <Input type="date" value={form.deposit_date}
                onChange={(e) => setForm({ ...form, deposit_date: e.target.value })}
                className="font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label className="eyebrow">Clearance Date</Label>
              <Input type="date" value={form.clearance_date}
                onChange={(e) => setForm({ ...form, clearance_date: e.target.value })}
                className="font-mono" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="eyebrow">Amount *</Label>
              <Input type="number" step="0.01" value={form.amount} required
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="font-mono" data-testid="cheque-amount" />
            </div>
            <div className="space-y-1.5">
              <Label className="eyebrow">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="cheque-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="eyebrow">Note</Label>
            <Textarea rows={2} value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </div>
          <FileAttachment
            label="Attach Cheque Scan / Photo"
            value={form.attachment_data_url || ""}
            onChange={(v) => setForm({ ...form, attachment_data_url: v })}
            testid="cheque-attachment"
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="cheque-save-btn">
              {saving ? "Saving…" : (editing ? "Save" : "Add")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Cheques() {
  const { activeCompanyId, activeStoreId, dateFloor } = useAuth();
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [alertLogs, setAlertLogs] = useState([]);
  const [sendingId, setSendingId] = useState(null);

  const load = async () => {
    if (!activeCompanyId) return;
    const params = new URLSearchParams();
    if (tab !== "all" && ["received", "issued"].includes(tab)) params.set("direction", tab);
    if (STATUSES.includes(tab)) params.set("status", tab);
    try {
      const [c, a] = await Promise.all([
        api.get(`/cheques${params.toString() ? "?" + params : ""}`),
        api.get("/cheques/alert-logs?limit=50").catch(() => ({ data: [] })),
      ]);
      setRows(c.data);
      setAlertLogs(a.data || []);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompanyId, activeStoreId, tab]);

  const sendAlert = async (id) => {
    setSendingId(id);
    try {
      const { data } = await api.post(`/cheques/${id}/send-alert`);
      if (data.status === "sent") toast.success("WhatsApp reminder sent");
      else if (data.status === "pending_manual") toast.info("Logged (Twilio not configured — send manually)");
      else toast.error(data.error || "Failed to send alert");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSendingId(null); }
  };

  const filtered = useMemo(() => {
    if (!q) return rows;
    const lq = q.toLowerCase();
    return rows.filter((r) =>
      (r.number || "").toLowerCase().includes(lq) ||
      (r.party_name || "").toLowerCase().includes(lq) ||
      (r.drawn_on_bank || "").toLowerCase().includes(lq)
    );
  }, [rows, q]);

  const kpis = useMemo(() => {
    const t = { pending: 0, deposited: 0, cleared: 0, bounced: 0, cancelled: 0 };
    rows.forEach((r) => { t[r.status] = (t[r.status] || 0) + Number(r.amount || 0); });
    return t;
  }, [rows]);

  const setStatus = async (id, status) => {
    const patch = { status };
    if (status === "deposited" && !rows.find((r) => r.id === id)?.deposit_date) patch.deposit_date = today();
    if ((status === "cleared" || status === "bounced")) patch.clearance_date = today();
    try {
      await api.patch(`/cheques/${id}`, patch);
      toast.success(`Marked ${status}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this cheque?")) return;
    try { await api.delete(`/cheques/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const exportColumns = [
    { key: "direction", label: "Direction" },
    { key: "number", label: "Cheque #" },
    { key: "drawn_on_bank", label: "Drawn on Bank" },
    { key: "party_name", label: "Party" },
    { key: "cheque_date", label: "Cheque Date" },
    { key: "deposit_date", label: "Deposit Date" },
    { key: "clearance_date", label: "Clearance Date" },
    { key: "amount", label: "Amount" },
    { key: "status", label: "Status" },
    { key: "note", label: "Note" },
  ];

  return (
    <div className="page-shell space-y-6" id="report-print-root">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow flex items-center gap-1.5"><Note size={11} /> Instruments</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Cheque Register</h1>
          <p className="text-sm text-muted-foreground mt-1">Every cheque received & issued — number, drawn-on-bank, deposit and clearance dates so nothing bounces silently.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap no-print">
          <PageExportBar
            title="Cheque Register"
            filename={`cheques-${today()}`}
            columns={exportColumns}
            rows={filtered}
            testidPrefix="cheques"
          />
          <Button className="h-9" onClick={() => { setEditing(null); setOpen(true); }} data-testid="new-cheque-btn">
            <Plus size={15} weight="bold" className="mr-1.5" /> Add Cheque
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="kpi" data-testid="cheque-kpi-pending"><div className="eyebrow" style={{ color: STATUS_COLOR.pending }}>Pending</div><div className="val">{inr(kpis.pending)}</div></div>
        <div className="kpi" data-testid="cheque-kpi-deposited"><div className="eyebrow">Deposited</div><div className="val">{inr(kpis.deposited)}</div></div>
        <div className="kpi" data-testid="cheque-kpi-cleared"><div className="eyebrow" style={{ color: STATUS_COLOR.cleared }}>Cleared</div><div className="val">{inr(kpis.cleared)}</div></div>
        <div className="kpi" data-testid="cheque-kpi-bounced"><div className="eyebrow" style={{ color: STATUS_COLOR.bounced }}>Bounced</div><div className="val">{inr(kpis.bounced)}</div></div>
        <div className="kpi" data-testid="cheque-kpi-cancelled"><div className="eyebrow">Cancelled</div><div className="val">{inr(kpis.cancelled)}</div></div>
      </div>

      <div className="card-flat p-4 flex flex-wrap items-center gap-3 no-print">
        <div className="relative">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search cheque #, party, bank…" className="pl-9 h-9 w-72" data-testid="cheques-search" />
        </div>
        <div className="text-sm text-muted-foreground ml-auto">{filtered.length} of {rows.length}</div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="cheques-tabs">
          <TabsTrigger value="all" data-testid="cheques-tab-all">All</TabsTrigger>
          <TabsTrigger value="received" data-testid="cheques-tab-received">Received</TabsTrigger>
          <TabsTrigger value="issued" data-testid="cheques-tab-issued">Issued</TabsTrigger>
          <TabsTrigger value="pending" data-testid="cheques-tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="cleared" data-testid="cheques-tab-cleared">Cleared</TabsTrigger>
          <TabsTrigger value="bounced" data-testid="cheques-tab-bounced">Bounced</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          <div className="card-flat overflow-hidden">
            <table className="data-table" data-testid="cheques-table">
              <thead>
                <tr>
                  <th>Cheque #</th>
                  <th>Direction</th>
                  <th>Drawn on Bank</th>
                  <th>Party</th>
                  <th>Cheque Date</th>
                  <th>Deposit</th>
                  <th>Clearance</th>
                  <th className="num">Amount</th>
                  <th>Status</th>
                  <th className="num" style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={10} className="text-center text-muted-foreground py-6">No cheques.</td></tr>
                ) : filtered.map((r) => (
                  <tr key={r.id} data-testid={`cheque-row-${r.id}`}>
                    <td className="font-mono">{r.number}</td>
                    <td className="text-[12px] uppercase tracking-wider">{r.direction}</td>
                    <td>{r.drawn_on_bank || "—"}</td>
                    <td>{r.party_name}</td>
                    <td className="font-mono">{fmtDate(r.cheque_date)}</td>
                    <td className="font-mono text-[12px]">{r.deposit_date || "—"}</td>
                    <td className="font-mono text-[12px]">{r.clearance_date || "—"}</td>
                    <td className="num font-mono">{inr(r.amount)}</td>
                    <td>
                      <span className="text-[10.5px] uppercase tracking-wider font-semibold" style={{ color: STATUS_COLOR[r.status] || "inherit" }}>{r.status}</span>
                    </td>
                    <td className="num" style={{ textAlign: "right" }}>
                      {r.status === "pending" && <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => setStatus(r.id, "deposited")} data-testid={`cheque-deposit-${r.id}`}>Deposit</Button>}
                      {(r.status === "pending" || r.status === "deposited") && <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => setStatus(r.id, "cleared")} data-testid={`cheque-clear-${r.id}`}>Clear</Button>}
                      {(r.status === "pending" || r.status === "deposited") && <Button size="sm" variant="ghost" className="h-7 text-[11px]" style={{ color: STATUS_COLOR.bounced }} onClick={() => setStatus(r.id, "bounced")} data-testid={`cheque-bounce-${r.id}`}>Bounce</Button>}
                      {(r.status === "pending" || r.status === "deposited") && <Button size="sm" variant="ghost" className="h-7" onClick={() => sendAlert(r.id)} disabled={sendingId === r.id} data-testid={`cheque-alert-${r.id}`} title="Send WhatsApp reminder now"><Broadcast size={12} /></Button>}
                      <Button size="sm" variant="ghost" className="h-7" onClick={() => { setEditing(r); setOpen(true); }} data-testid={`cheque-edit-${r.id}`}><PencilSimple size={12} /></Button>
                      <Button size="sm" variant="ghost" className="h-7" onClick={() => del(r.id)} data-testid={`cheque-delete-${r.id}`}><Trash size={12} /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <ChequeForm open={open} setOpen={setOpen} editing={editing} onSaved={load} />

      {/* Alert Log Panel */}
      <div className="card-flat overflow-hidden" data-testid="alert-log-panel">
        <div className="p-4 border-b border-border flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="eyebrow flex items-center gap-1.5"><Broadcast size={11} /> Reminders</div>
            <div className="font-heading text-lg font-semibold">Recent WhatsApp Alerts</div>
            <div className="text-[11.5px] text-muted-foreground mt-0.5">Last {alertLogs.length} entries — auto-scans run hourly plus manual sends.</div>
          </div>
          <Button variant="outline" size="sm" className="h-8" onClick={load} data-testid="alert-log-reload">Reload</Button>
        </div>
        <table className="data-table" data-testid="alert-log-table">
          <thead>
            <tr>
              <th>Fired At</th>
              <th>Cheque #</th>
              <th>Party</th>
              <th>Phone</th>
              <th>Channel</th>
              <th>Status</th>
              <th className="num">Days to Clear</th>
              <th>Error / Provider ID</th>
            </tr>
          </thead>
          <tbody>
            {alertLogs.length === 0 ? (
              <tr><td colSpan={8} className="text-center text-muted-foreground py-6">No alerts fired yet.</td></tr>
            ) : alertLogs.map((l) => {
              const ok = l.status === "sent";
              const pending = l.status === "pending_manual";
              const color = ok ? "hsl(var(--success))" : pending ? "hsl(var(--warning))" : "hsl(var(--destructive))";
              return (
                <tr key={l.id} data-testid={`alert-log-row-${l.id}`}>
                  <td className="font-mono text-[12px]">{String(l.fired_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="font-mono">{l.cheque_number || "—"}</td>
                  <td>{l.party_name || "—"}</td>
                  <td className="font-mono text-[12px]">{l.phone || "—"}</td>
                  <td className="text-[11.5px] uppercase tracking-wider">{l.channel}</td>
                  <td>
                    <span className="text-[10.5px] uppercase tracking-wider font-semibold flex items-center gap-1" style={{ color }}>
                      {ok ? <CheckCircle size={11} weight="fill" /> : <Warning size={11} weight="fill" />}
                      {l.status}
                    </span>
                  </td>
                  <td className="num font-mono">{l.days_before_clearance !== undefined ? `${l.days_before_clearance}d` : "—"}</td>
                  <td className="font-mono text-[11px] text-muted-foreground">{l.error || l.provider_message_id || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
